// Versão: 3.16
// Data..: 2026-10-13
// Layout corporativo: sidebar escura fixa com grupos de navegação + topbar + busca rápida.
// v3.16: removido o item "Tarefas" (/tasks) do grupo Atendimento; Tarefas virou aba em
// "Agendamentos" (/schedule).
// v3.15: Ponto Eletrônico — item "Ponto" (grupo Gestão, abaixo de "Painel gerencial"),
// visível para ADMIN/AGENT/ACCOUNTING via `roles`; a contabilidade (ACCOUNTING) só
// enxerga "Ponto" (os demais itens staff/públicos ficam ocultos para esse papel).
// v3.14: removido o item "Agenda" (/calendar); o calendário Zimbra passou para "Agendamentos".
// v3.13: item "Agenda" (menu, staff) no grupo Atendimento — compromissos do calendário Zimbra.
// v3.12: "Financeiro API" saiu do menu Gestão — agora é aba em Administração > Sistema.
// v3.11: item "Financeiro API" (adminOnly) no grupo Gestão, abaixo de Painel gerencial.
// Conteúdo principal fluido/responsivo (ocupa a largura da tela com limite amplo).
// v3.09: botão para recolher/expandir a sidebar (mesmo botão alterna; estado persistido).
// v3.10: navegação mobile — em telas estreitas a sidebar vira drawer sobreposto,
// aberto por um botão hambúrguer na topbar (fecha por backdrop, ESC e ao navegar).

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { UserRole } from '@makweb/shared';
import { useAuth } from '../context/AuthContext.js';
import { useTheme } from '../context/ThemeContext.js';
import { CommandPalette } from './CommandPalette.js';
import { NotificationBell } from './NotificationBell.js';

// Chave de persistência do estado recolhido da sidebar.
const SIDEBAR_KEY = 'makweb.sidebar.collapsed';

// Navegação agrupada por seção. `roles` restringe o item a papéis específicos (ex.: o
// Ponto, visível também para a contabilidade, que não é staff).
const groups: { label: string; items: { to: string; label: string; icon: string; staff?: boolean; adminOnly?: boolean; roles?: UserRole[] }[] }[] = [
  {
    label: 'Atendimento',
    items: [
      { to: '/', label: 'Painel', icon: '▤' },
      { to: '/central', label: 'Minha Central', icon: '🎯', staff: true },
      { to: '/tickets', label: 'Chamados', icon: '🎫' },
      { to: '/whatsapp', label: 'WhatsApp', icon: '💬', staff: true },
      { to: '/schedule', label: 'Agendamentos', icon: '🗓', staff: true },
    ],
  },
  {
    label: 'Conhecimento',
    items: [{ to: '/kb', label: 'Base de conhecimento', icon: '📖' }],
  },
  {
    label: 'Gestão',
    items: [
      { to: '/management', label: 'Painel gerencial', icon: '📊', staff: true },
      { to: '/timeclock', label: 'Ponto', icon: '🕒', roles: [UserRole.ADMIN, UserRole.AGENT, UserRole.ACCOUNTING] },
      { to: '/admin', label: 'Administração', icon: '⚙', staff: true },
    ],
  },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const isAdmin = user?.role === UserRole.ADMIN;
  // A contabilidade (ACCOUNTING) é um papel restrito: só pode ver os itens de menu que a
  // listam explicitamente em `roles` (hoje, apenas o "Ponto").
  const isAccounting = user?.role === UserRole.ACCOUNTING;

  // Sidebar recolhível: quando recolhida, mostra só os ícones (largura reduzida).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0'); } catch { /* ignora */ }
      return next;
    });
  };

  // Drawer de navegação no mobile (a sidebar fixa é ocultada em telas estreitas).
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Fecha o drawer ao navegar entre páginas.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Fecha o drawer com a tecla ESC e trava o scroll do body enquanto aberto.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  const roleLabel =
    user?.role === UserRole.ADMIN ? 'Administrador' : user?.role === UserRole.AGENT ? 'Atendente' : 'Cliente';

  // Sidebar é clara no tema claro (logo colorida) e escura no tema escuro (logo branca).
  const sidebarLogo = theme === 'dark' ? '/logo-dark.png' : '/logo-light.png';

  const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
    display: 'flex', alignItems: 'center', gap: '11px',
    padding: '9px 11px', borderRadius: '10px', fontSize: '13.5px', fontWeight: isActive ? 600 : 500,
    color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
    background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
    boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
    transition: 'all 0.18s ease',
    justifyContent: collapsed ? 'center' : 'flex-start',
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Backdrop do drawer no mobile (só aparece quando aberto). */}
      {mobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar escura (recolhível: só ícones quando collapsed).
          No mobile vira drawer sobreposto controlado por `mobileOpen`. */}
      <aside
        className={`app-sidebar ${mobileOpen ? 'is-open' : ''}`}
        style={{
          width: collapsed ? 72 : 244, background: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)',
          display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh',
          boxShadow: '2px 0 12px rgba(15, 23, 42, 0.04)', zIndex: 6,
          transition: 'width 0.2s ease',
        }}
      >
        <div
          className="flex items-center"
          style={{
            padding: collapsed ? '18px 0 14px' : '18px 18px 14px', borderBottom: '1px solid var(--sidebar-border)',
            justifyContent: collapsed ? 'center' : 'space-between', gap: 8,
          }}
        >
          {!collapsed && <img src={sidebarLogo} alt="Makweb" style={{ height: 26, width: 'auto', display: 'block' }} />}
          <button
            onClick={toggleSidebar}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-expanded={!collapsed}
            style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0, border: 'none', cursor: 'pointer',
              display: 'grid', placeItems: 'center', fontSize: 15,
              background: 'var(--surface-3)', color: 'var(--sidebar-text)', transition: 'all 0.18s ease',
            }}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>

        <nav style={{ padding: '12px 12px', flex: 1, overflowY: 'auto' }}>
          {groups.map((g) => {
            const items = g.items.filter((i) => {
              // Item com `roles`: visível só para os papéis listados.
              if (i.roles) return !!user && i.roles.includes(user.role);
              // Itens sem `roles` nunca aparecem para a contabilidade (papel restrito).
              if (isAccounting) return false;
              return (!i.staff || isStaff) && (!i.adminOnly || isAdmin);
            });
            if (!items.length) return null;
            return (
              <div key={g.label} style={{ marginBottom: 'var(--s4)' }}>
                {/* Rótulo do grupo some quando recolhido (só ícones). */}
                {!collapsed && (
                  <div style={{ padding: '4px 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sidebar-text-dim)' }}>
                    {g.label}
                  </div>
                )}
                <div className="flex flex-col gap-1 mt-2">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      style={navLinkStyle}
                      className="nav-item"
                      title={collapsed ? item.label : undefined}
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            style={{
                              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                              display: 'grid', placeItems: 'center', fontSize: 14,
                              background: isActive ? 'var(--gradient-primary)' : 'var(--surface-3)',
                              color: isActive ? '#fff' : 'var(--sidebar-text)',
                              boxShadow: isActive ? '0 2px 8px rgba(0,136,204,0.35)' : 'none',
                              transition: 'all 0.18s ease',
                            }}
                          >
                            {item.icon}
                          </span>
                          {!collapsed && item.label}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Rodapé: cartão de usuário (link para conta) + botão Sair separado.
            Recolhido: só avatar + botão sair, empilhados e centralizados. */}
        <div style={{ padding: '10px', borderTop: '1px solid var(--sidebar-border)' }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '8px 0' : '8px 10px',
              borderRadius: 12, background: 'var(--surface-3)', marginBottom: 8,
              flexDirection: collapsed ? 'column' : 'row',
              justifyContent: 'center',
            }}
          >
            <NavLink
              to="/account"
              title="Minha conta"
              className="flex items-center gap-2"
              style={{ flex: collapsed ? undefined : 1, minWidth: 0, color: 'var(--sidebar-text)', justifyContent: 'center' }}
            >
              <span
                style={{
                  width: 38, height: 38, borderRadius: '50%', background: 'var(--gradient-primary)',
                  display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 15,
                  flexShrink: 0, boxShadow: '0 2px 8px rgba(0,136,204,0.35)',
                }}
              >
                {(user?.name ?? '?').charAt(0).toUpperCase()}
              </span>
              {!collapsed && (
                <div style={{ overflow: 'hidden', lineHeight: 1.3 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--sidebar-text-dim)' }}>{roleLabel}</div>
                </div>
              )}
            </NavLink>
            <button
              onClick={signOut}
              title="Sair"
              aria-label="Sair"
              style={{
                width: 34, height: 34, borderRadius: 9, flexShrink: 0, border: 'none', cursor: 'pointer',
                display: 'grid', placeItems: 'center', fontSize: 15,
                background: 'transparent', color: 'var(--sidebar-text-dim)', transition: 'all 0.18s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--sidebar-text-dim)'; }}
            >
              ⏻
            </button>
          </div>
        </div>
      </aside>

      {/* Conteúdo */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          className="flex items-center justify-between"
          style={{ padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 5 }}
        >
          <div className="flex items-center gap-2">
            {/* Abre a navegação no mobile (oculto no desktop). */}
            <button
              className="btn btn-icon btn-ghost show-mobile"
              onClick={() => setMobileOpen(true)}
              title="Abrir menu"
              aria-label="Abrir menu"
              aria-expanded={mobileOpen}
            >
              ☰
            </button>
            <button
              className="btn btn-sm btn-ghost hide-mobile"
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
              title="Busca rápida (Ctrl+K)"
              style={{ color: 'var(--text-3)' }}
            >
              🔍 Buscar <span className="text-dim" style={{ marginLeft: 6 }}>Ctrl K</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <a href="/portal" target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost hide-mobile">Portal público ↗</a>
            <button className="btn btn-icon btn-ghost" onClick={toggle} title="Alternar tema">{theme === 'light' ? '🌙' : '☀️'}</button>
            <NotificationBell />
            <NavLink to="/tickets/new" className="btn btn-primary btn-sm">+ Novo chamado</NavLink>
          </div>
        </header>
        <main className="app-main fade-in">{children}</main>
      </div>
      <CommandPalette />
    </div>
  );
}
